/**
 * NiV Clinic Detail — "פרטי הקליניקה" tab (Oct 2026).
 * Everything the system knows about the clinic in ONE scroll:
 *   flags strip → KPI tiles → identity → activity → usage telemetry →
 *   quotas → messaging → features → clinic income → team → system record
 * A sticky jump bar (SectionJumpBar) tracks the visible section.
 */

import { useMemo } from "react";
import {
  Users, CalendarBlank, User, ChatCircleDots, Brain, PenNib, VideoCamera, Tag, Timer, Info,
  WarningCircle, Flag, Lightbulb, Prohibit, SignOut, CalendarX, SignIn,
} from "@phosphor-icons/react";
import HoverTip from "../../../../../components/ui/HoverTip";
import useCountUp from "../../shared/hooks/useCountUp";
import { getPaymentAlert, getPaymentPlanHint, getSeatMismatch, isChurnedClinic } from "../../shared/nivDerived";
import { DETAIL_SECTIONS, formatDate, formatLimit, getDaysUntilTrialEnd, planToneClass } from "../clinicDetailConstants";
import { isUnlimited } from "../../../../../services/planLimits";
import OverrideFlag from "../components/OverrideFlag";
import LoginGrid from "../components/LoginGrid";
import LastPatientCard from "../components/LastPatientCard";
import DailyBarsCard from "../components/DailyBarsCard";
import MoneyByMonthChart from "../components/MoneyByMonthChart";
import UsageSection from "../components/UsageSection";
import MessagingSection from "../components/MessagingSection";
import SectionJumpBar, { sectionDomId } from "../components/SectionJumpBar";
import { IdentityCard, SystemCard, TeamCard } from "../components/ClinicInfoCards";
import { TelemetryFeaturesSection, TelemetryUsageSection } from "../components/TelemetryBlocks";

function Kpi({ icon: Icon, value, limit, label, tone = "", info, flagDiffs, animate = true }) {
  const numeric = typeof value === "number";
  const shown = useCountUp(numeric && animate ? value : 0);
  return (
    <div className={`ncd2-kpi ${tone}`}>
      <span className="ncd2-kpi-icon">
        <Icon size={16} weight="fill" />
      </span>
      <div className="ncd2-kpi-info">
        <span className="ncd2-kpi-value">
          {numeric ? shown.toLocaleString("he-IL") : value}
          {limit != null && <span className="ncd2-kpi-limit">/{limit}</span>}
          {flagDiffs?.length > 0 && <OverrideFlag diffs={flagDiffs} size={11} />}
        </span>
        <span className="ncd2-kpi-label">
          {label}
          {info && (
            <HoverTip text={info}>
              <Info size={12} className="ncd2-kpi-info-icon" />
            </HoverTip>
          )}
        </span>
      </div>
    </div>
  );
}

function Section({ id, title, sub, children }) {
  const meta = DETAIL_SECTIONS.find((s) => s.id === id);
  return (
    <section id={sectionDomId(id)} className="ncd2-section">
      <div className="ncd2-section-head">
        <h2 className="ncd2-section-title">{title || meta?.label}</h2>
        {sub && <span className="ncd2-muted ncd2-note">{sub}</span>}
      </div>
      {children}
    </section>
  );
}

/** Every indication the system has about this clinic, as square chips. */
const buildFlags = ({ clinic, crm, usage, onGoTab }) => {
  const flags = [];
  const row = { ...clinic, crm };
  const diffs = clinic.limitDiffs || [];
  const seat = getSeatMismatch({ crm, activeTherapistsCount: clinic.activeTherapistsCount, therapistsCount: clinic.therapistsCount });
  const payAlert = getPaymentAlert(row);
  const planHint = getPaymentPlanHint(row);
  const days = clinic.plan === "trial" ? getDaysUntilTrialEnd(clinic.trialEndDate) : null;
  const loginDays = usage?.logins?.daysSinceLastLogin;
  const nextScheduled = usage?.meetings?.next30d?.scheduled;

  if (clinic.inactiveByAdmin) flags.push({ key: "inactive", tone: "danger", icon: Prohibit, label: "הושבתה ע״י מנהל" });
  if (payAlert) flags.push({ key: "pay", tone: "warning", icon: WarningCircle, label: payAlert.label, onClick: () => onGoTab("manage") });
  if (planHint) flags.push({ key: "hint", tone: "info", icon: Lightbulb, label: planHint.label, onClick: () => onGoTab("manage") });
  if (seat) flags.push({ key: "seats", tone: "warning", icon: Users, label: `${seat.active}/${seat.paid} מושבים — פער ${seat.excess}`, onClick: () => onGoTab("manage") });
  if (days !== null && days <= 0) flags.push({ key: "trial", tone: "danger", icon: Timer, label: "הניסיון פג" });
  else if (days !== null && days <= 7) flags.push({ key: "trial", tone: "warning", icon: Timer, label: `הניסיון מסתיים בעוד ${days} ימים` });
  if (isChurnedClinic(row)) flags.push({ key: "churned", tone: "danger", icon: SignOut, label: "נטשה — היה משלם" });
  if (crm?.paymentStatus === "left") flags.push({ key: "left", tone: "muted", icon: SignOut, label: "סומנה כעזבה" });
  if (diffs.length > 0) flags.push({ key: "overrides", tone: "override", icon: Flag, label: `${diffs.length} חריגות ממסלול`, onClick: () => onGoTab("manage") });
  if (usage && (loginDays == null || Number(loginDays) >= 30)) {
    flags.push({ key: "login", tone: "danger", icon: SignIn, label: loginDays == null ? "מעולם לא נכנסו" : `ללא כניסה ${loginDays} ימים` });
  }
  if (usage && Number(nextScheduled) === 0) flags.push({ key: "nomeet", tone: "warning", icon: CalendarX, label: "אין פגישות ב-30 הימים הקרובים" });
  return flags;
};

export default function ClinicDetailsTab({ clinic, crm, activity, activityLoading, telemetry, telemetryLoading, refetch, onCopyId, onGoTab }) {
  const eff = clinic.effectiveLimits || {};
  const diffByKey = Object.fromEntries((clinic.limitDiffs || []).map((d) => [d.key, d]));
  const flagsFor = (...keys) => keys.map((k) => diffByKey[k]).filter(Boolean);
  const usage = telemetry?.usage || null;

  const daysUntilTrialEnd = clinic.plan === "trial" ? getDaysUntilTrialEnd(clinic.trialEndDate) : null;
  const trialExpired = daysUntilTrialEnd !== null && daysUntilTrialEnd <= 0;
  const trialSoon = daysUntilTrialEnd !== null && daysUntilTrialEnd > 0 && daysUntilTrialEnd <= 7;

  const flags = useMemo(() => buildFlags({ clinic, crm, usage, onGoTab }), [clinic, crm, usage, onGoTab]);
  const sections = useMemo(
    () => DETAIL_SECTIONS.map((s) => (s.id === "flags" ? { ...s, count: flags.length } : s)),
    [flags.length]
  );

  return (
    <div className="ncd2-panel ncd2-details">
      <SectionJumpBar sections={sections} />

      {/* 1. Flags & indications */}
      <Section id="flags" title="דגלים ואינדיקציות">
        {flags.length === 0 ? (
          <div className="ncd2-flags-ok">אין דגלים — הכל תקין</div>
        ) : (
          <div className="ncd2-flags">
            {flags.map((f) => {
              const Icon = f.icon;
              const cls = `ncd2-flag-chip ncd2-flag-${f.tone}`;
              return f.onClick ? (
                <button key={f.key} type="button" className={cls} onClick={f.onClick}>
                  <Icon size={13} weight="fill" />
                  {f.label}
                </button>
              ) : (
                <span key={f.key} className={cls}>
                  <Icon size={13} weight="fill" />
                  {f.label}
                </span>
              );
            })}
          </div>
        )}
      </Section>

      {/* 2. KPIs */}
      <Section id="kpis" title="מדדים">
        <div className="ncd2-kpi-grid">
          <Kpi icon={Users} value={clinic.activePatients ?? clinic.totalPatients} limit={formatLimit(eff.maxPatients)} label={`מטופלים פעילים (${clinic.totalPatients} סה"כ)`} flagDiffs={flagsFor("maxPatients")} />
          <Kpi icon={CalendarBlank} value={clinic.appointmentsLast30Days || 0} label="פגישות (30 יום)" />
          <Kpi
            icon={User}
            value={clinic.activeTherapistsCount ?? clinic.therapistsCount}
            limit={formatLimit(eff.maxTherapists)}
            label={clinic.activeTherapistsCount != null && clinic.activeTherapistsCount !== clinic.therapistsCount ? `מטפלים פעילים (${clinic.therapistsCount} סה"כ)` : "מטפלים"}
            flagDiffs={flagsFor("maxTherapists", "canAddTherapists")}
          />
          <Kpi
            icon={ChatCircleDots}
            value={clinic.currentMonthSmsSent || 0}
            limit={formatLimit(eff.smsPerMonth)}
            label="SMS החודש"
            flagDiffs={flagsFor("smsPerMonth")}
            tone={!isUnlimited(eff.smsPerMonth) && (clinic.currentMonthSmsSent || 0) >= eff.smsPerMonth ? "is-danger" : ""}
          />
          <Kpi icon={Brain} value={clinic.currentMonthAiRequests || 0} limit={formatLimit(eff.aiActionsPerMonth)} label="בקשות AI החודש" flagDiffs={flagsFor("aiActionsPerMonth")} />
          <Kpi icon={PenNib} value={clinic.currentMonthDigitalSignatures || 0} label="חתימות דיגיטליות החודש" />
          <Kpi icon={VideoCamera} value={clinic.currentMonthVideoMinutes || 0} limit={eff.videoMinutesPerMonth} label="דקות וידאו החודש" flagDiffs={flagsFor("videoMinutesPerMonth")} />
          <Kpi
            icon={Tag}
            value={clinic.planName}
            label={clinic.planEndDate ? `מסלול · פעיל עד ${formatDate(clinic.planEndDate)}` : "מסלול"}
            tone={planToneClass(clinic.plan)}
            flagDiffs={clinic.limitDiffs || []}
            animate={false}
          />
          {clinic.plan === "trial" && (
            <Kpi
              icon={Timer}
              value={trialExpired ? "פג" : `${daysUntilTrialEnd ?? "—"} ימים`}
              label={`ניסיון · עד ${formatDate(clinic.trialEndDate)}`}
              tone={trialExpired ? "is-danger" : trialSoon ? "is-warning" : ""}
              info={`${clinic.trialWriteOps || 0} / ${eff.maxWriteOpsPerTrial || 3000} פעולות כתיבה בניסיון`}
              animate={false}
            />
          )}
        </div>
      </Section>

      {/* 3. Identity */}
      <Section id="identity" title="פרטים">
        <IdentityCard clinic={clinic} refetch={refetch} />
      </Section>

      {/* 4. Activity */}
      <Section id="activity" title="פעילות" sub="כניסות, מטופל אחרון ופעולות יומיות (30 יום)">
        <div className="ncd2-two-col">
          <LoginGrid therapistDaily={telemetry?.therapistDaily} isLoading={telemetryLoading} />
          <LastPatientCard lastPatient={activity?.lastPatient} isLoading={activityLoading} totalPatients={clinic.totalPatients} activePatients={clinic.activePatients} />
        </div>
        <DailyBarsCard daily30={activity?.daily30} isLoading={activityLoading} />
      </Section>

      {/* 5. Usage telemetry */}
      <Section id="usage" title="שימוש במערכת" sub="כניסות, אינטגרציות ופגישות — niv_clinic_usage">
        <TelemetryUsageSection clinicId={clinic.clinicId} usage={usage} />
      </Section>

      {/* 6. Quotas */}
      <Section id="quotas" title="מכסות ושימוש חודשי">
        <UsageSection clinic={clinic} />
      </Section>

      {/* 7. Messaging */}
      <Section id="messaging" title="הודעות (WhatsApp / SMS)">
        <MessagingSection clinicId={clinic.clinicId} />
      </Section>

      {/* 8. Features & pages */}
      <Section id="features" title="פיצ'רים ועמודים">
        <TelemetryFeaturesSection clinicId={clinic.clinicId} usage={usage} />
      </Section>

      {/* 9. Clinic's own income */}
      <Section id="finance" title="הכנסות הקליניקה מהמטופלים" sub="הכנסות הקליניקה עצמה מהמטופלים שלה — לא הכנסות CliniKit">
        <MoneyByMonthChart moneyByMonth={activity?.moneyByMonth} isLoading={activityLoading} />
      </Section>

      {/* 10. Team */}
      <Section id="team" title="צוות והרשאות">
        <TeamCard clinic={clinic} />
      </Section>

      {/* 11. System record */}
      <Section id="system" title="נתוני מערכת">
        <SystemCard clinic={clinic} onCopyId={onCopyId} />
      </Section>
    </div>
  );
}
